import React, {Fragment} from "react";
import {css} from "@emotion/react";

import {mb, mt, pointer, underline} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {identity} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Currency} from "@pg-rp/common-types";

import {ShowMoreButton} from "../../atoms/ShowMoreButton";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {preLineDescription} from "../../offer/detail/components/OfferDetailDescription";
import {getParkingPlacesType} from "../../offer/detail/constants/ParkingPlacesType";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {IOfferGroupsSimplified} from "../../offer/detail/types/IOfferGroups";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferType} from "../../offer/types/OfferType";
import {IRangeQuery} from "../../request/IRangeQuery";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {VendorType} from "../../vendor/helpers/VendorType";
import {actualStreetNameCheck, getPropertyHouseTypeWithoutNoun} from "../helpers/generated_decription/generated_description";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";
import {IParkingPlace} from "../interfaces/IParkingPlace";
import {IRoomArea, IRoomAreaLabel} from "../interfaces/IRoomArea";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailRegion} from "../types/IPropertyDetailRegion";
import {IPropertyTag} from "../types/IPropertyTag";
import {PropertyDetailGeneratedDescriptionCommercial} from "./PropertyDetailGeneratedDescriptionCommercial";
import {PropertyDetailGeneratedDescriptionFlat} from "./PropertyDetailGeneratedDescriptionFlat";
import {PropertyDetailGeneratedDescriptionHouses} from "./PropertyDetailGeneratedDescriptionHouses";
import {PropertyDetailTags} from "./PropertyDetailTags";

interface IProps {
    variables: {
        property_additional_areas: IValueLabel[];
        property_kitchen_type: IValueLabel[];
        property_room_area_type: IValueLabel[];
        property_quarters: IValueLabel[];
    } | null;
    property: {
        additional_areas: {
            type: number;
            area: number;
        }[];
        area: number;
        configuration: {
            show_price: boolean;
            show_price_m2: boolean;
        };
        construction_end_date: string;
        description: string;
        floor: number;
        floors: number;
        garages: number;
        height_range: {
            lower: number;
            upper: number;
        };
        has_attic: boolean;
        has_basement: boolean;
        house_type: number;
        kitchen_type: number;
        number: string;
        offer: {
            buildings: number;
            currency: Currency;
            construction_date_range: IRangeQuery<string>;
            configuration: {
                is_autoresponder_enabled: boolean;
                display_type: OfferDisplayType;
                show_generated_description: boolean;
            };
            description: string;
            floors_above_ground_range: IRangeQuery<number> | null;
            floors_below_ground: number;
            groups: IOfferGroupsSimplified | null;
            id: number;
            investor: string | null;
            is_condohotel: boolean;
            is_holiday_location: boolean;
            name: string;
            parking_places: IParkingPlace[];
            price_type: number | null;
            properties: number;
            region: IPropertyDetailRegion;
            slug: string;
            stats: {
                distance_from_region: {
                    center: boolean;
                    distance: number;
                    region: string;
                };
                properties_count_for_sale: number;
                ranges_area_max: number;
                ranges_area_min: number;
                ranges_height_max: number;
                ranges_height_min: number;
            } | null;
            storage: {
                quantity: number;
                price: number;
            };
            street_name: string;
            street_number: string;
            tags: IPropertyTag[];
            type: number;
            vendor: {
                slug: string;
                name: string;
                type: VendorType;
            };
        };
        plot_area: number;
        price: number | null;
        price_m2: number;
        rooms: number;
        room_areas: {
            type: number;
            area: number;
        }[];
        quarters: number[];
        similar_properties: number | null;
        tags: IPropertyTag[];
    };
}

export const PropertyDetailDescription = (props: IProps) => {
    const {property, variables} = props;

    const doesPropertyHaveRealStreet = actualStreetNameCheck(property.offer.street_name);
    const {updateUserPreferences} = useUserSearchPreferences();

    const offerAddress = (
        <span>
            , {property.offer.street_name} {property.offer.street_number}
        </span>
    );
    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const offerUrl = useOfferLink(property.offer);

    const roomAreaList = property.room_areas.reduce((acc: IRoomAreaLabel[], aA: IRoomArea): IRoomAreaLabel[] => {
        const roomAreaTypes = variables && variables.property_room_area_type;
        const foundRoomAreas = roomAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundRoomAreas ? ([...acc, {area: aA.area, type: foundRoomAreas.label}] as IRoomAreaLabel[]) : acc;
    }, []);

    const additionalAreaList = property.additional_areas.reduce((acc: IAdditionalArea[], aA: IAdditionalArea): IAdditionalArea[] => {
        const additionalAreaTypes = variables && variables.property_additional_areas;
        const foundAdditionalAreas = additionalAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundAdditionalAreas
            ? ([
                  ...acc,
                  {
                      area: aA.area,
                      type: foundAdditionalAreas.label
                  }
              ] as IAdditionalArea[])
            : acc;
    }, []);

    const quarterTypes = variables && variables.property_quarters;
    const foundQuarterTypes = property.quarters.map((q) => quarterTypes?.find((qT) => qT.value === q));
    const quarterTypeList = foundQuarterTypes.map((item) => (item && item.label.toLowerCase()) || "").filter(identity) as string[];

    const parkingPlacesQuantity = pluralize(["miejsce", "miejsca", "miejsc"]);
    const renderParkingPlaces = property.offer.parking_places.map(
        (parkingPlace: IParkingPlace) =>
            `${getParkingPlacesType(parkingPlace.type).toLowerCase()}${
                parkingPlace.quantity ? ` (${parkingPlace.quantity} ${parkingPlacesQuantity(parkingPlace.quantity)})` : ""
            }`
    );

    const kitchenTypes = variables && variables.property_kitchen_type;
    const foundKitchenType = kitchenTypes?.find((kT) => kT.value === property.kitchen_type);
    const kitchenType: string = (foundKitchenType && foundKitchenType.label) || "";
    const houseTypeWithoutNoun = property.house_type ? getPropertyHouseTypeWithoutNoun(property.house_type) : "";

    const textAfterInvestorOrVendorName = `${property.offer.investor || property.offer.vendor.name}`.endsWith(".") ? "" : ".";
    const offerInvestorOrVendor = `${property.offer.investor || property.offer.vendor.name}${textAfterInvestorOrVendorName}`;

    const renderDescription = (offerType: OfferType) => {
        const descriptionFlat = `${
            isApartment ? `Nowy ${property.rooms}-pokojowy apartament` : `Nowe ${property.rooms}-pokojowe mieszkanie`
        } na sprzedaż o powierzchni ${areaFormat(property.area, {precision: 2})}`;
        const descriptionHouse = `Nowy ${property.rooms}-pokojowy dom ${houseTypeWithoutNoun} na sprzedaż o powierzchni ${areaFormat(property.area, {
            precision: 2
        })}`;
        const descriptionCommercial = `Nowy lokal użytkowy na sprzedaż o powierzchni ${areaFormat(property.area, {precision: 2})}`;
        switch (offerType) {
            case OfferType.FLAT:
                return descriptionFlat;
            case OfferType.HOUSE:
                return descriptionHouse;
            case OfferType.COMMERCIAL:
                return descriptionCommercial;
            default:
                throw new Error("Unknown offer type");
        }
    };

    const target = useResponsiveLinkTarget();

    const onSimilarPropertiesClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        updateUserPreferences({rooms_0: property.rooms.toString(), rooms_1: property.rooms.toString()});
        window.open(offerUrl, target);
    };

    const renderSimilarProperties = () => {
        if (property.similar_properties) {
            return (
                <Fragment>
                    , w której {pluralize(["znajduje", "znajdują", "znajduje"])(property.similar_properties)} się w sprzedaży jeszcze{" "}
                    <a onClick={onSimilarPropertiesClick} css={link}>
                        {property.similar_properties}
                        {property.offer.type === OfferType.HOUSE
                            ? pluralize([" podobny nowy dom", " podobne nowe domy", " podobnych nowych domów"])(property.similar_properties)
                            : isApartment
                              ? pluralize([" podobny nowy apartament", " podobne nowe apartamenty", " podobnych nowych apartamentów"])(
                                    property.similar_properties
                                )
                              : pluralize([" podobne nowe mieszkanie", " podobne nowe mieszkania", " podobnych nowych mieszkań"])(property.similar_properties)}
                    </a>
                </Fragment>
            );
        }

        return null;
    };

    const renderGeneratedDescription = (type: OfferType) => {
        switch (type) {
            case OfferType.FLAT:
                return (
                    <PropertyDetailGeneratedDescriptionFlat
                        property={property}
                        roomAreaList={roomAreaList}
                        additionalAreaList={additionalAreaList}
                        quarterTypeList={quarterTypeList}
                        renderParkingPlaces={renderParkingPlaces}
                        kitchenType={kitchenType}
                        showDescriptionPrice={showDescriptionPrice}
                    />
                );
            case OfferType.HOUSE:
                return (
                    <PropertyDetailGeneratedDescriptionHouses
                        property={property}
                        roomAreaList={roomAreaList}
                        additionalAreaList={additionalAreaList}
                        quarterTypeList={quarterTypeList}
                        renderParkingPlaces={renderParkingPlaces}
                        showDescriptionPrice={showDescriptionPrice}
                    />
                );
            case OfferType.COMMERCIAL:
                return (
                    <PropertyDetailGeneratedDescriptionCommercial
                        property={property}
                        renderParkingPlaces={renderParkingPlaces}
                        showDescriptionPrice={showDescriptionPrice}
                    />
                );
            default:
                throw new Error("Unknown offer type");
        }
    };

    const fullProperty = property as IPropertyDetail;
    const shouldShowPriceFromABTest = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: fullProperty.offer.configuration.price_visibility,
        vendorPriceVisibility: fullProperty.offer.vendor.configuration.price_visibility,
        vendorId: fullProperty.offer.vendor.id
    });

    const showDescriptionPrice = !property.offer.configuration.is_autoresponder_enabled && property.price !== null && shouldShowPriceFromABTest;

    return (
        <>
            <Text as="h2" css={mb(2)} variant="headline_4">
                Opis oferty
            </Text>

            {property.description && <div>{preLineDescription(property.description)}</div>}

            <Text mb={2} variant="body_copy_1">
                {renderDescription(property.offer.type)} znajduje się w nowo {property.construction_end_date ? "wybudowanej" : "budowanej"} inwestycji
                deweloperskiej <span className="bt">{property.offer.name}</span> {property.offer.region.name_declension_where}
                {property.offer.street_name && doesPropertyHaveRealStreet && offerAddress}
                {property.offer.stats?.distance_from_region &&
                    property.offer.stats.distance_from_region.distance > 0 &&
                    ` (${property.offer.stats.distance_from_region.distance} km od ${property.offer.stats.distance_from_region.center ? `centrum` : ""} ${
                        property.offer.stats.distance_from_region.region
                    }).`}
            </Text>
            <Text variant="body_copy_1" mb={2}>
                {property.offer.vendor.type === VendorType.DEVELOPER ? (
                    <>
                        Inwestycja {property.construction_end_date ? "została zrealizowana" : "jest realizowana"} przez {offerInvestorOrVendor}
                    </>
                ) : (
                    <>
                        Sprzedaż {property.offer.configuration.display_type === OfferDisplayType.SOLD ? "prowadziła" : "prowadzi"} {offerInvestorOrVendor}
                    </>
                )}
            </Text>

            {property.offer.description ? (
                <>
                    {property.offer.configuration.show_generated_description && (
                        <ShowMoreButton
                            css={mb(3)}
                            dataGtmClick="Czytaj więcej - opis inwestycji"
                            preventShowLess
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.DESCRIPTION_READ_MORE_BUTTON}
                        >
                            {renderGeneratedDescription(property.offer.type)}
                        </ShowMoreButton>
                    )}
                    <Text as="h2" variant="headline_4">
                        Opis Inwestycji
                    </Text>
                    <PropertyDetailTags property={property} />
                    <div css={mt(2)}>{preLineDescription(property.offer.description)}</div>
                </>
            ) : (
                <>{renderGeneratedDescription(property.offer.type)}</>
            )}

            {(property.offer.type === OfferType.FLAT || property.offer.type === OfferType.HOUSE) && (
                <Text css={mt(2)} variant="body_copy_1">
                    {isApartment ? "Apartament " : `${getOfferTypeNameCapital(property.offer.type)} `}
                    znajduje się w inwestycji{" "}
                    <a css={link} href={offerUrl} target={target} className="bt">
                        {property.offer.name}
                    </a>
                    {renderSimilarProperties()}.
                </Text>
            )}
        </>
    );
};

const link = css`
    ${pointer};

    &,
    &:hover,
    &:active,
    &:focus {
        ${underline};
    }
`;
import React, {Fragment} from "react";
import {css} from "@emotion/react";

import {mb, mt, pointer, underline} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {identity} from "@pg-mono/nodash";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {Currency} from "@pg-rp/common-types";

import {ShowMoreButton} from "../../atoms/ShowMoreButton";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {preLineDescription} from "../../offer/detail/components/OfferDetailDescription";
import {getParkingPlacesType} from "../../offer/detail/constants/ParkingPlacesType";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {IOfferGroupsSimplified} from "../../offer/detail/types/IOfferGroups";
import {OfferDisplayType} from "../../offer/helpers/OfferDisplayType";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferType} from "../../offer/types/OfferType";
import {IRangeQuery} from "../../request/IRangeQuery";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {VendorType} from "../../vendor/helpers/VendorType";
import {actualStreetNameCheck, getPropertyHouseTypeWithoutNoun} from "../helpers/generated_decription/generated_description";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";
import {IParkingPlace} from "../interfaces/IParkingPlace";
import {IRoomArea, IRoomAreaLabel} from "../interfaces/IRoomArea";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyDetailRegion} from "../types/IPropertyDetailRegion";
import {IPropertyTag} from "../types/IPropertyTag";
import {PropertyDetailGeneratedDescriptionCommercial} from "./PropertyDetailGeneratedDescriptionCommercial";
import {PropertyDetailGeneratedDescriptionFlat} from "./PropertyDetailGeneratedDescriptionFlat";
import {PropertyDetailGeneratedDescriptionHouses} from "./PropertyDetailGeneratedDescriptionHouses";
import {PropertyDetailTags} from "./PropertyDetailTags";

interface IProps {
    variables: {
        property_additional_areas: IValueLabel[];
        property_kitchen_type: IValueLabel[];
        property_room_area_type: IValueLabel[];
        property_quarters: IValueLabel[];
    } | null;
    property: {
        additional_areas: {
            type: number;
            area: number;
        }[];
        area: number;
        configuration: {
            show_price: boolean;
            show_price_m2: boolean;
        };
        construction_end_date: string;
        description: string;
        floor: number;
        floors: number;
        garages: number;
        height_range: {
            lower: number;
            upper: number;
        };
        has_attic: boolean;
        has_basement: boolean;
        house_type: number;
        kitchen_type: number;
        number: string;
        offer: {
            buildings: number;
            currency: Currency;
            construction_date_range: IRangeQuery<string>;
            configuration: {
                is_autoresponder_enabled: boolean;
                display_type: OfferDisplayType;
                show_generated_description: boolean;
            };
            description: string;
            floors_above_ground_range: IRangeQuery<number> | null;
            floors_below_ground: number;
            groups: IOfferGroupsSimplified | null;
            id: number;
            investor: string | null;
            is_condohotel: boolean;
            is_holiday_location: boolean;
            name: string;
            parking_places: IParkingPlace[];
            price_type: number | null;
            properties: number;
            region: IPropertyDetailRegion;
            slug: string;
            stats: {
                distance_from_region: {
                    center: boolean;
                    distance: number;
                    region: string;
                };
                properties_count_for_sale: number;
                ranges_area_max: number;
                ranges_area_min: number;
                ranges_height_max: number;
                ranges_height_min: number;
            } | null;
            storage: {
                quantity: number;
                price: number;
            };
            street_name: string;
            street_number: string;
            tags: IPropertyTag[];
            type: number;
            vendor: {
                slug: string;
                name: string;
                type: VendorType;
            };
        };
        plot_area: number;
        price: number | null;
        price_m2: number;
        rooms: number;
        room_areas: {
            type: number;
            area: number;
        }[];
        quarters: number[];
        similar_properties: number | null;
        tags: IPropertyTag[];
    };
}

export const PropertyDetailDescription = (props: IProps) => {
    const {property, variables} = props;

    const doesPropertyHaveRealStreet = actualStreetNameCheck(property.offer.street_name);
    const {updateUserPreferences} = useUserSearchPreferences();

    const offerAddress = (
        <span>
            , {property.offer.street_name} {property.offer.street_number}
        </span>
    );
    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const offerUrl = useOfferLink(property.offer);

    const roomAreaList = property.room_areas.reduce((acc: IRoomAreaLabel[], aA: IRoomArea): IRoomAreaLabel[] => {
        const roomAreaTypes = variables && variables.property_room_area_type;
        const foundRoomAreas = roomAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundRoomAreas ? ([...acc, {area: aA.area, type: foundRoomAreas.label}] as IRoomAreaLabel[]) : acc;
    }, []);

    const additionalAreaList = property.additional_areas.reduce((acc: IAdditionalArea[], aA: IAdditionalArea): IAdditionalArea[] => {
        const additionalAreaTypes = variables && variables.property_additional_areas;
        const foundAdditionalAreas = additionalAreaTypes?.find((aAType) => aAType.value === aA.type);
        return foundAdditionalAreas
            ? ([
                  ...acc,
                  {
                      area: aA.area,
                      type: foundAdditionalAreas.label
                  }
              ] as IAdditionalArea[])
            : acc;
    }, []);

    const quarterTypes = variables && variables.property_quarters;
    const foundQuarterTypes = property.quarters.map((q) => quarterTypes?.find((qT) => qT.value === q));
    const quarterTypeList = foundQuarterTypes.map((item) => (item && item.label.toLowerCase()) || "").filter(identity) as string[];

    const parkingPlacesQuantity = pluralize(["miejsce", "miejsca", "miejsc"]);
    const renderParkingPlaces = property.offer.parking_places.map(
        (parkingPlace: IParkingPlace) =>
            `${getParkingPlacesType(parkingPlace.type).toLowerCase()}${
                parkingPlace.quantity ? ` (${parkingPlace.quantity} ${parkingPlacesQuantity(parkingPlace.quantity)})` : ""
            }`
    );

    const kitchenTypes = variables && variables.property_kitchen_type;
    const foundKitchenType = kitchenTypes?.find((kT) => kT.value === property.kitchen_type);
    const kitchenType: string = (foundKitchenType && foundKitchenType.label) || "";
    const houseTypeWithoutNoun = property.house_type ? getPropertyHouseTypeWithoutNoun(property.house_type) : "";

    const textAfterInvestorOrVendorName = `${property.offer.investor || property.offer.vendor.name}`.endsWith(".") ? "" : ".";
    const offerInvestorOrVendor = `${property.offer.investor || property.offer.vendor.name}${textAfterInvestorOrVendorName}`;

    const renderDescription = (offerType: OfferType) => {
        const descriptionFlat = `${
            isApartment ? `Nowy ${property.rooms}-pokojowy apartament` : `Nowe ${property.rooms}-pokojowe mieszkanie`
        } na sprzedaż o powierzchni ${areaFormat(property.area, {precision: 2})}`;
        const descriptionHouse = `Nowy ${property.rooms}-pokojowy dom ${houseTypeWithoutNoun} na sprzedaż o powierzchni ${areaFormat(property.area, {
            precision: 2
        })}`;
        const descriptionCommercial = `Nowy lokal użytkowy na sprzedaż o powierzchni ${areaFormat(property.area, {precision: 2})}`;
        switch (offerType) {
            case OfferType.FLAT:
                return descriptionFlat;
            case OfferType.HOUSE:
                return descriptionHouse;
            case OfferType.COMMERCIAL:
                return descriptionCommercial;
            default:
                throw new Error("Unknown offer type");
        }
    };

    const target = useResponsiveLinkTarget();

    const onSimilarPropertiesClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        updateUserPreferences({rooms_0: property.rooms.toString(), rooms_1: property.rooms.toString()});
        window.open(offerUrl, target);
    };

    const renderSimilarProperties = () => {
        if (property.similar_properties) {
            return (
                <Fragment>
                    , w której {pluralize(["znajduje", "znajdują", "znajduje"])(property.similar_properties)} się w sprzedaży jeszcze{" "}
                    <a onClick={onSimilarPropertiesClick} css={link}>
                        {property.similar_properties}
                        {property.offer.type === OfferType.HOUSE
                            ? pluralize([" podobny nowy dom", " podobne nowe domy", " podobnych nowych domów"])(property.similar_properties)
                            : isApartment
                              ? pluralize([" podobny nowy apartament", " podobne nowe apartamenty", " podobnych nowych apartamentów"])(
                                    property.similar_properties
                                )
                              : pluralize([" podobne nowe mieszkanie", " podobne nowe mieszkania", " podobnych nowych mieszkań"])(property.similar_properties)}
                    </a>
                </Fragment>
            );
        }

        return null;
    };

    const renderGeneratedDescription = (type: OfferType) => {
        switch (type) {
            case OfferType.FLAT:
                return (
                    <PropertyDetailGeneratedDescriptionFlat
                        property={property}
                        roomAreaList={roomAreaList}
                        additionalAreaList={additionalAreaList}
                        quarterTypeList={quarterTypeList}
                        renderParkingPlaces={renderParkingPlaces}
                        kitchenType={kitchenType}
                        showDescriptionPrice={showDescriptionPrice}
                    />
                );
            case OfferType.HOUSE:
                return (
                    <PropertyDetailGeneratedDescriptionHouses
                        property={property}
                        roomAreaList={roomAreaList}
                        additionalAreaList={additionalAreaList}
                        quarterTypeList={quarterTypeList}
                        renderParkingPlaces={renderParkingPlaces}
                        showDescriptionPrice={showDescriptionPrice}
                    />
                );
            case OfferType.COMMERCIAL:
                return (
                    <PropertyDetailGeneratedDescriptionCommercial
                        property={property}
                        renderParkingPlaces={renderParkingPlaces}
                        showDescriptionPrice={showDescriptionPrice}
                    />
                );
            default:
                throw new Error("Unknown offer type");
        }
    };

    const fullProperty = property as IPropertyDetail;
    const shouldShowPriceFromABTest = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: fullProperty.offer.configuration.price_visibility,
        vendorPriceVisibility: fullProperty.offer.vendor.configuration.price_visibility,
        vendorId: fullProperty.offer.vendor.id
    });

    const showDescriptionPrice = !property.offer.configuration.is_autoresponder_enabled && property.price !== null && shouldShowPriceFromABTest;

    return (
        <>
            <Text as="h2" css={mb(2)} variant="headline_4">
                Opis oferty
            </Text>

            {property.description && <div>{preLineDescription(property.description)}</div>}

            <Text mb={2} variant="body_copy_1">
                {renderDescription(property.offer.type)} znajduje się w nowo {property.construction_end_date ? "wybudowanej" : "budowanej"} inwestycji
                deweloperskiej <span className="bt">{property.offer.name}</span> {property.offer.region.name_declension_where}
                {property.offer.street_name && doesPropertyHaveRealStreet && offerAddress}
                {property.offer.stats?.distance_from_region &&
                    property.offer.stats.distance_from_region.distance > 0 &&
                    ` (${property.offer.stats.distance_from_region.distance} km od ${property.offer.stats.distance_from_region.center ? `centrum` : ""} ${
                        property.offer.stats.distance_from_region.region
                    }).`}
            </Text>
            <Text variant="body_copy_1" mb={2}>
                {property.offer.vendor.type === VendorType.DEVELOPER ? (
                    <>
                        Inwestycja {property.construction_end_date ? "została zrealizowana" : "jest realizowana"} przez {offerInvestorOrVendor}
                    </>
                ) : (
                    <>
                        Sprzedaż {property.offer.configuration.display_type === OfferDisplayType.SOLD ? "prowadziła" : "prowadzi"} {offerInvestorOrVendor}
                    </>
                )}
            </Text>

            {property.offer.description ? (
                <>
                    {property.offer.configuration.show_generated_description && (
                        <ShowMoreButton
                            css={mb(3)}
                            dataGtmClick="Czytaj więcej - opis inwestycji"
                            preventShowLess
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.DESCRIPTION_READ_MORE_BUTTON}
                        >
                            {renderGeneratedDescription(property.offer.type)}
                        </ShowMoreButton>
                    )}
                    <Text as="h2" variant="headline_4">
                        Opis Inwestycji
                    </Text>
                    <PropertyDetailTags property={property} />
                    <div css={mt(2)}>{preLineDescription(property.offer.description)}</div>
                </>
            ) : (
                <>{renderGeneratedDescription(property.offer.type)}</>
            )}

            {(property.offer.type === OfferType.FLAT || property.offer.type === OfferType.HOUSE) && (
                <Text css={mt(2)} variant="body_copy_1">
                    {isApartment ? "Apartament " : `${getOfferTypeNameCapital(property.offer.type)} `}
                    znajduje się w inwestycji{" "}
                    <a css={link} href={offerUrl} target={target} className="bt">
                        {property.offer.name}
                    </a>
                    {renderSimilarProperties()}.
                </Text>
            )}
        </>
    );
};

const link = css`
    ${pointer};

    &,
    &:hover,
    &:active,
    &:focus {
        ${underline};
    }
`;
